// Step Select Web Component
class StepSelectComponent extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 0;
    this.selectedFilters = {};
    this.totalSteps = 0;
    this.filters = [];
    this.isLoading = false;
    this.showResultsTimeout = null;
  }

  static get observedAttributes() {
    return ['data-filters', 'data-current-step'];
  }

  connectedCallback() {
    this.initializeComponent();
    this.setupEventListeners();
    this.restoreStateFromURL();
  }

  initializeComponent() {
    // Parse filters from data attribute
    const filtersData = this.getAttribute('data-filters');
    if (filtersData) {
      try {
        this.filters = JSON.parse(filtersData);
        this.totalSteps = this.filters.length;
      } catch (error) {
        console.error('Error parsing filters:', error);
        this.filters = [];
        this.totalSteps = 0;
      }
    } else {
      this.filters = [];
      this.totalSteps = 0;
    }

    // Initialize current step
    const currentStepAttr = this.getAttribute('data-current-step');
    if (currentStepAttr) {
      this.currentStep = parseInt(currentStepAttr);
    }

    // Initialize settings
    this.settings = {
      primaryColor: this.getAttribute('data-primary-color') || '#c54343',
      secondaryColor: this.getAttribute('data-secondary-color') || '#0f4c82',
      showProductCount: this.getAttribute('data-show-product-count') === 'true'
    };

    this.render();
  }

  updateStepsProgress() {
    // Update the steps component based on URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const totalSteps = this.filters.length;
    const componentId = `steps-60-${totalSteps}`;
    
    // Count completed steps based on URL parameters
    let completedSteps = 0;
    this.filters.forEach((filter, index) => {
      let hasSelection = false;
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          hasSelection = true;
        }
      });
      if (hasSelection) {
        completedSteps++;
      }
    });
    
    if (window.updateStepsProgress) {
      window.updateStepsProgress(componentId, completedSteps);
    }
  }

  render() {
    // HTML is now rendered by Liquid template
    // Just apply styles and update state
    this.applyStyles();
    this.updateStepDisplay();
    
  }

  applyStyles() {
    const container = this.querySelector('#step-select-container');
    if (!container) return;

    // Set CSS custom properties for colors
    container.style.setProperty('--primary-color', this.settings.primaryColor);
    container.style.setProperty('--secondary-color', this.settings.secondaryColor);
    
    // Calculate darker shades for hover effects
    const primaryColorDark = this.darkenColor(this.settings.primaryColor, 20);
    const secondaryColorDark = this.darkenColor(this.settings.secondaryColor, 20);
    
    container.style.setProperty('--primary-color-dark', primaryColorDark);
    container.style.setProperty('--secondary-color-dark', secondaryColorDark);

  }

  // Helper function to darken a color
  darkenColor(color, percent) {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
      (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
      (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
  }

  getStepClass(stepIndex) {
    if (stepIndex < this.currentStep) return 'completed';
    if (stepIndex === this.currentStep) return 'active';
    return '';
  }

  isSelected(stepIndex, value) {
    return this.selectedFilters[stepIndex] && this.selectedFilters[stepIndex].value === value;
  }


  setupEventListeners() {
    
    // Use document-level event delegation for better reliability
    document.addEventListener('click', (e) => {
      // Check if the click is within this component
      if (!this.contains(e.target)) {
        return;
      }
      
      const option = e.target.closest('.filter-option');
      if (option && !this.isLoading) {
        this.selectOption(option);
        return;
      }

      // Handle navigation buttons
      const button = e.target.closest('.step-button');
      if (button && !this.isLoading) {
        const action = button.dataset.action;
        const step = button.dataset.step;
        
        if (action === 'goToStep' && step !== undefined) {
          this.goToStep(parseInt(step));
        } else if (action === 'backToFilters') {
          this.backToFilters();
        }
      }
    });

    // Handle browser back/forward
    window.addEventListener('popstate', () => {
      this.restoreStateFromURL();
    });

    // Also listen for URL changes (for cases where URL is updated programmatically)
    let lastUrl = window.location.href;
    const urlChangeObserver = new MutationObserver(() => {
      const currentUrl = window.location.href;
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        this.restoreStateFromURL();
      }
    });
    
    // Observe changes to the document title (which often changes with URL)
    urlChangeObserver.observe(document, { subtree: true, childList: true });
  }

  async selectOption(option) {
    const stepIndex = parseInt(option.dataset.step);
    const value = option.dataset.value;
    const param = option.dataset.param;
    const label = option.dataset.label;

    // Save selection
    this.selectedFilters[stepIndex] = {
      label: label,
      value: value,
      param: param
    };

    // Immediately add selected class for visual feedback
    option.classList.add('selected');

    // Update URL
    this.updateURL();
    
    // Manually trigger DOM update since pushState doesn't trigger popstate
    await this.restoreStateFromURL();
  }

  async updatePageContent() {
    if (this.isLoading) return;
    
    this.isLoading = true;
    this.showLoading();

    try {
      const baseUrl = window.location.pathname;
      const params = new URLSearchParams();
      
      // Add selected filters to URL params
      Object.values(this.selectedFilters).forEach(selection => {
        params.append(selection.param, selection.value);
      });

      const newUrl = `${baseUrl}?${params.toString()}`;
      
      // Fetch updated page content
      const response = await fetch(newUrl);
      const responseText = await response.text();
      const newDocument = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Find the step-select-component in the new document
      const newComponent = newDocument.querySelector('step-select-component');
      if (newComponent) {
        // Parse the new filters data
        const newFiltersData = newComponent.getAttribute('data-filters');
        if (newFiltersData) {
          const newFilters = JSON.parse(newFiltersData);
          
          // Update the filters array
          this.filters = newFilters;
          this.totalSteps = this.filters.length;
          
          // Update the entire container with new content
          const newContainer = newDocument.getElementById('step-select-container');
          const currentContainer = this.querySelector('#step-select-container');
          if (newContainer && currentContainer) {
            currentContainer.innerHTML = newContainer.innerHTML;
            this.applyStyles();
          }
          
          // Update step display
          this.updateStepDisplay();
        }
      }

    } catch (error) {
      // Handle error silently
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  showProductResults() {
    if (this.showResultsTimeout) {
      clearTimeout(this.showResultsTimeout);
    }
    
    this.showResultsTimeout = setTimeout(() => {
      this._doShowProductResults();
    }, 300);
  }

  async _doShowProductResults() {
    this.isLoading = true;
    this.showLoading();

    try {
      const baseUrl = window.location.pathname;
      const params = new URLSearchParams();
      
      // Add selected filters to URL params
      Object.values(this.selectedFilters).forEach(selection => {
        params.append(selection.param, selection.value);
      });

      // Fetch product grid section
      const url = `${baseUrl}?section_id=main-collection-product-grid&${params.toString()}`;
      const response = await fetch(url);
      const html = await response.text();
      
      // Update product results
      const productGridContent = this.querySelector('#product-grid-content');
      if (productGridContent) {
        productGridContent.innerHTML = html;
      }

      // Show results
      const stepContainer = this.querySelector('.step-content-container');
      const productResults = this.querySelector('.product-results');
      
      if (stepContainer) {
        stepContainer.style.display = 'none';
      }
      if (productResults) {
        productResults.classList.add('active');
      }

    } catch (error) {
      console.error(error)
    } finally {
      this.isLoading = false;
      this.hideLoading();
    }
  }

  backToFilters() {
    // 清除防抖定时器
    if (this.showResultsTimeout) {
      clearTimeout(this.showResultsTimeout);
      this.showResultsTimeout = null;
    }
    
    // Show step content, hide results
    const stepContainer = this.querySelector('.step-content-container');
    const productResults = this.querySelector('.product-results');
    
    if (stepContainer) stepContainer.style.display = 'block';
    if (productResults) productResults.classList.remove('active');
    
    // Clear URL parameters
    window.history.pushState({}, '', window.location.pathname);
    
    // Reset state
    this.selectedFilters = {};
    this.currentStep = 0;
    this.updateStepDisplay();
    
    // Clear selected states in DOM
    this.markSelectedOptions();
  }

  goToStep(stepNumber) {
    if (stepNumber < this.currentStep) {
      // Going back - clear subsequent selections
      Object.keys(this.selectedFilters).forEach(stepIndex => {
        if (parseInt(stepIndex) >= stepNumber) {
          delete this.selectedFilters[stepIndex];
        }
      });

      // Update URL
      this.updateURL();
      
      // Fetch updated content for subsequent steps
      this.updateSubsequentSteps(stepNumber);
    }

    this.currentStep = stepNumber;
    this.updateStepDisplay();
  }

  async updateSubsequentSteps(fromStep) {
    try {
      const baseUrl = window.location.pathname;
      const params = new URLSearchParams();
      
      // Add filters up to the target step
      Object.keys(this.selectedFilters).forEach(stepIndex => {
        if (parseInt(stepIndex) < fromStep) {
          const selection = this.selectedFilters[stepIndex];
          params.append(selection.param, selection.value);
        }
      });

      const newUrl = `${baseUrl}?${params.toString()}`;
      const response = await fetch(newUrl);
      const responseText = await response.text();
      const newDocument = new DOMParser().parseFromString(responseText, 'text/html');
      
      // Update subsequent steps
      for (let i = fromStep; i < this.totalSteps; i++) {
        const newStep = newDocument.querySelector(`.step-content[data-step="${i}"]`);
        const currentStep = this.querySelector(`.step-content[data-step="${i}"]`);
        
        if (newStep && currentStep) {
          currentStep.innerHTML = newStep.innerHTML;
        }
      }

    } catch (error) {
      // Handle error silently or show user-friendly message
    }
  }

  updateStepDisplay() {
    // Update step content visibility
    this.querySelectorAll('.step-content').forEach((content, index) => {
      content.classList.toggle('active', index === this.currentStep);
    });

    // Update steps component
    this.updateStepsProgress();
  }

  updateURL() {
    const baseUrl = window.location.pathname;
    const params = new URLSearchParams();
    
    Object.values(this.selectedFilters).forEach(selection => {
      params.append(selection.param, selection.value);
    });

    const newUrl = `${baseUrl}?${params.toString()}`;
    window.history.pushState({}, '', newUrl);
  }

  async restoreStateFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const restoredSelections = {};
    
    // Parse URL parameters and restore selections
    this.filters.forEach((filter, index) => {
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          restoredSelections[index] = {
            label: value.label,
            value: value.value,
            param: value.param_name
          };
        }
      });
    });

    // Update selected filters
    this.selectedFilters = restoredSelections;
    
    // Determine current step and update DOM
    if (Object.keys(restoredSelections).length > 0) {
      // Check if all steps are completed
      const completedSteps = Object.keys(restoredSelections).map(Number);
      const allStepsCompleted = completedSteps.length === this.totalSteps && 
        completedSteps.every(step => step >= 0 && step < this.totalSteps);
      
      if (allStepsCompleted) {
        // All steps completed, update progress and show product results
        this.updateStepsProgress();
        this.showProductResults();
      } else {
        // Some steps completed, show the next incomplete step
        const lastCompletedStep = Math.max(...completedSteps);
        this.currentStep = lastCompletedStep + 1;
        
        // If we're beyond the last step, show product results
        if (this.currentStep >= this.totalSteps) {
          this.showProductResults();
        } else {
          // Update DOM with new content and mark selections
          await this.updatePageContent();
          this.markSelectedOptions();
        }
      }
    } else {
      // No selections, start from step 0
      this.currentStep = 0;
      
      // Hide product results and show step content when going back to filters
      const stepContainer = this.querySelector('.step-content-container');
      const productResults = this.querySelector('.product-results');
      
      if (stepContainer) {
        stepContainer.style.display = 'block';
      }
      if (productResults) {
        productResults.classList.remove('active');
      }
      
      this.updateStepDisplay();
      this.markSelectedOptions();
    }
  }

  markSelectedOptions() {
    // Clear all selected states first
    this.querySelectorAll('.filter-option').forEach(option => {
      option.classList.remove('selected');
    });

    // Clear all select dropdown selections
    this.querySelectorAll('.filter-select').forEach(select => {
      select.selectedIndex = 0;
    });

    // Mark options as selected based on URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    
    this.filters.forEach((filter, filterIndex) => {
      filter.values.forEach(value => {
        if (urlParams.has(value.param_name) && urlParams.get(value.param_name) === value.value) {
          // Find the corresponding option in the DOM
          const stepContent = this.querySelector(`.step-content[data-step="${filterIndex}"]`);
          if (stepContent) {
            // Handle card options
            const option = stepContent.querySelector(`[data-value="${value.value}"]`);
            if (option) {
              option.classList.add('selected');
            }
            
            // Handle select dropdown
            const select = stepContent.querySelector('.filter-select');
            if (select) {
              const optionElement = select.querySelector(`option[value="${value.value}"]`);
              if (optionElement) {
                optionElement.selected = true;
              }
            }
          }
        }
      });
    });
  }

  showLoading() {
    const loadingElements = this.querySelectorAll('.loading');
    loadingElements.forEach(el => el.style.display = 'block');
  }

  hideLoading() {
    const loadingElements = this.querySelectorAll('.loading');
    loadingElements.forEach(el => el.style.display = 'none');
  }
}

// Register the custom element when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    customElements.define('step-select-component', StepSelectComponent);
  });
} else {
  customElements.define('step-select-component', StepSelectComponent);
}
